import { gql, useMutation, useQuery } from '@apollo/client';
import {
  Box,
  Button,
  Heading,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import dayjs from 'dayjs';
import { dinero, isZero } from 'dinero.js';
import NextLink from 'next/link';
import { useRouter } from 'next/router';
import * as React from 'react';

import { CurrencyDisplay } from '@/components/Currency';
import {
  MembershipTermLifecycleStatus,
  MembershipTermPaymentAlert,
  MembershipTermRenewalSetting,
} from '@/graphql-types/globalTypes';
import { useMemberManagement } from '@/hooks/useMemberManagement';
import { Emoji } from '@/imports/ui/chakra/components/Emoji';
import { isGreaterThanZero } from '@/utilities';

import { MembershipRenewalModal_CurrentUser } from './graphql-types/MembershipRenewalModal_CurrentUser';
import {
  MembershipRenewalModal_SetMembershipOrderRenewalModalSeen,
  MembershipRenewalModal_SetMembershipOrderRenewalModalSeenVariables,
} from './graphql-types/MembershipRenewalModal_SetMembershipOrderRenewalModalSeen';

const MEMBERSHIP_TERM = gql`
  fragment MembershipRenewalModal_MembershipTermFragment on MembershipTermV2 {
    membershipType {
      _id
      name
    }
    startDate
    lifecycleStatus
    paymentAlert
    renewalSetting
    owner {
      ... on User {
        _id
      }
      ... on Organization {
        _id
      }
    }
    paymentOrder {
      _id
      paymentDueDate
      renewalModalSeen
      price
      paymentDue
      paymentReceived
      creditToApply
      paymentRemaining
    }
  }
`;

const GET_USER = gql`
  query MembershipRenewalModal_CurrentUser {
    currentUser {
      _id
      firstName
      rosterOrganization {
        _id
        membershipData {
          currentMembershipTerm {
            _id
            ...MembershipRenewalModal_MembershipTermFragment
          }
        }
      }
      membershipData {
        currentMembershipTerm {
          _id
          ...MembershipRenewalModal_MembershipTermFragment
        }
      }
    }
  }
  ${MEMBERSHIP_TERM}
`;

const MARK_AS_SEEN = gql`
  mutation MembershipRenewalModal_SetMembershipOrderRenewalModalSeen(
    $input: SetMembershipOrderRenewalModalSeenInput!
  ) {
    setMembershipOrderRenewalModalSeen(input: $input) {
      ... on SetMembershipOrderRenewalModalSeenSuccess {
        membershipOrder {
          _id
          renewalModalSeen
        }
      }
    }
  }
`;

export const MembershipRenewalModal: React.FC = () => {
  const router = useRouter();

  const successfulMembershipOrderId =
    router.query.successfulMembershipOrderId?.toString();

  const isShowingMembershipSuccessModal = !!successfulMembershipOrderId;

  const memberManagement = useMemberManagement();

  const modal = useDisclosure();

  const query = useQuery<MembershipRenewalModal_CurrentUser>(GET_USER, {
    skip: !memberManagement.isActive,
  });

  const [markRenewalAsSeen] = useMutation<
    MembershipRenewalModal_SetMembershipOrderRenewalModalSeen,
    MembershipRenewalModal_SetMembershipOrderRenewalModalSeenVariables
  >(MARK_AS_SEEN);

  const currentUser = query.data?.currentUser;

  const currentIndividualMembershipTerm =
    currentUser?.membershipData?.currentMembershipTerm;

  const currentCorporateMembershipTerm =
    currentUser?.rosterOrganization?.membershipData?.currentMembershipTerm;

  const relevantMembershipTerm = React.useMemo(() => {
    if (
      currentCorporateMembershipTerm &&
      currentCorporateMembershipTerm.paymentOrder &&
      currentCorporateMembershipTerm.paymentOrder.renewalModalSeen === false
    ) {
      return currentCorporateMembershipTerm;
    }

    if (
      currentIndividualMembershipTerm &&
      currentIndividualMembershipTerm.paymentOrder &&
      currentIndividualMembershipTerm.paymentOrder.renewalModalSeen === false
    ) {
      return currentIndividualMembershipTerm;
    }

    return undefined;
  }, [currentCorporateMembershipTerm, currentIndividualMembershipTerm]);

  const hasAchPaymentAlert =
    !!relevantMembershipTerm?.paymentAlert &&
    [
      MembershipTermPaymentAlert.ACH_PROCESSING,
      MembershipTermPaymentAlert.ACH_VERIFICATION_NEEDED,
    ].includes(relevantMembershipTerm.paymentAlert);

  const shouldShowModalForRenewalOrGrace =
    (relevantMembershipTerm?.lifecycleStatus ===
      MembershipTermLifecycleStatus.RENEWING &&
      relevantMembershipTerm.renewalSetting !==
        MembershipTermRenewalSetting.AUTOMATIC_RENEWAL) ||
    relevantMembershipTerm?.lifecycleStatus ===
      MembershipTermLifecycleStatus.GRACED;

  React.useEffect(() => {
    if (
      !isShowingMembershipSuccessModal &&
      relevantMembershipTerm &&
      !hasAchPaymentAlert &&
      shouldShowModalForRenewalOrGrace &&
      relevantMembershipTerm.paymentOrder &&
      relevantMembershipTerm.paymentOrder.renewalModalSeen === false
    ) {
      modal.onOpen();
    }
  }, [
    relevantMembershipTerm,
    modal,
    hasAchPaymentAlert,
    isShowingMembershipSuccessModal,
    shouldShowModalForRenewalOrGrace,
  ]);

  if (!memberManagement.isActive) {
    return null;
  }

  if (!currentUser) {
    return null;
  }

  if (!relevantMembershipTerm) {
    return null;
  }

  if (!relevantMembershipTerm.paymentOrder) {
    return null;
  }

  const price = dinero(relevantMembershipTerm.paymentOrder.price);
  const creditToApply = dinero(
    relevantMembershipTerm.paymentOrder.creditToApply
  );
  const paymentDue = dinero(relevantMembershipTerm.paymentOrder.paymentDue);
  const paymentRemaining = dinero(
    relevantMembershipTerm.paymentOrder.paymentRemaining
  );

  const canPayInFullWithCredit =
    isZero(paymentDue) && isGreaterThanZero(creditToApply);

  const canCompleteAsFree = isZero(paymentDue) && isZero(price);

  const handleCloseModal = async () => {
    modal.onClose();
    if (relevantMembershipTerm.paymentOrder) {
      markRenewalAsSeen({
        variables: {
          input: {
            membershipOrderId: relevantMembershipTerm.paymentOrder._id,
          },
        },
      });
    }
  };

  return (
    <Modal
      isOpen={modal.isOpen}
      onClose={handleCloseModal}
      size="md"
      isCentered
    >
      <ModalOverlay />
      <ModalContent p="6" textAlign="center">
        <ModalHeader p="0">
          <Stack spacing="4">
            <Emoji symbol="👋" fontSize="3xl" />

            <Box>
              <Heading size="md">Hi {currentUser.firstName}!</Heading>
              <Heading size="md">
                {relevantMembershipTerm.lifecycleStatus ===
                MembershipTermLifecycleStatus.RENEWING
                  ? `Your ${
                      relevantMembershipTerm.owner.__typename === 'Organization'
                        ? `Company's `
                        : ''
                    }${
                      relevantMembershipTerm.membershipType.name
                    } is Up for Renewal`
                  : relevantMembershipTerm.lifecycleStatus ===
                    MembershipTermLifecycleStatus.GRACED
                  ? `Your ${
                      relevantMembershipTerm.owner.__typename === 'Organization'
                        ? `Company's `
                        : ''
                    }${relevantMembershipTerm.membershipType.name} is Past Due`
                  : ''}
              </Heading>
            </Box>
          </Stack>
        </ModalHeader>

        <ModalCloseButton />

        <ModalBody pt="4" pb="6" px="0">
          {!canPayInFullWithCredit && !canCompleteAsFree && (
            <Stack spacing="0">
              <CurrencyDisplay
                value={paymentRemaining}
                textStyle="xlBodyStrong"
              />
              <Text textStyle="compact" color="gray.600">
                Payment due{' '}
                {dayjs(
                  relevantMembershipTerm.paymentOrder.paymentDueDate
                ).format('MMM D, YYYY')}
              </Text>
            </Stack>
          )}
        </ModalBody>

        <Stack>
          <NextLink
            href={`/membership-orders/${relevantMembershipTerm.paymentOrder._id}/pay`}
          >
            <Button>
              {relevantMembershipTerm.lifecycleStatus ===
              MembershipTermLifecycleStatus.GRACED
                ? 'Pay Now'
                : 'Renew Now'}
            </Button>
          </NextLink>
          <Link
            as={NextLink}
            href={
              relevantMembershipTerm.owner.__typename === 'Organization'
                ? '/my-company/membership'
                : '/me/membership'
            }
          >
            Manage Membership
          </Link>
        </Stack>
      </ModalContent>
    </Modal>
  );
};
